import type { EndTurnCommand, MatchState } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  appendLog,
  drawCards,
  err,
  getOtherSeatId,
  getSeat,
  updateInstance,
  updateSeat,
} from "../state"

export function handleEndTurn(
  state: MatchState,
  cmd: EndTurnCommand,
): CommandResult {
  if (state.turn.step !== "main") {
    return err(
      "END_TURN_WRONG_STEP",
      "Can only end turn during the main action window.",
    )
  }

  let s = state
  const seatId = cmd.seatId

  // 1. Discard all cards remaining in hand
  s = discardHand(s, seatId)

  // 2. Expire temporary modifiers
  s = expireModifiers(s, seatId)

  // 3. Log end of turn
  s = appendLog(s, seatId, "end_turn", { turn: state.turn.number })

  // 4. Advance to next player
  const nextSeatId = getOtherSeatId(seatId)
  const nextTurnNumber =
    nextSeatId === "seat_a" ? state.turn.number + 1 : state.turn.number

  // 5. Start next turn: recover, energy, draw
  s = startTurn(s, nextSeatId, nextTurnNumber)

  return { ok: true, state: s }
}

function discardHand(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): MatchState {
  const seat = getSeat(state, seatId)
  const toDiscard = seat.zones.hand
  if (toDiscard.length === 0) return state

  let s = updateSeat(state, seatId, (seat) => ({
    ...seat,
    zones: {
      ...seat.zones,
      hand: [],
      discard: [...seat.zones.discard, ...toDiscard],
    },
  }))

  for (const id of toDiscard) {
    s = updateInstance(s, id, (inst) => ({ ...inst, zone: "discard" }))
  }

  return s
}

function expireModifiers(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): MatchState {
  const currentTurn = state.turn.number
  let s = state

  for (const [instanceId, inst] of Object.entries(s.instances)) {
    const remainingModifiers = inst.modifiers.filter(
      (m) =>
        m.expiresAfterTurn === undefined ||
        m.expiresAfterTurn > currentTurn ||
        (m.expiresAfterSeatId !== undefined && m.expiresAfterSeatId !== seatId),
    )
    if (remainingModifiers.length !== inst.modifiers.length) {
      const expiredModifiers = inst.modifiers.filter(
        (m) =>
          m.expiresAfterTurn !== undefined &&
          m.expiresAfterTurn <= currentTurn &&
          (m.expiresAfterSeatId === undefined ||
            m.expiresAfterSeatId === seatId),
      )
      const expiredAttack = expiredModifiers.reduce(
        (sum, modifier) => sum + modifier.attackMod,
        0,
      )
      const expiredHealth = expiredModifiers.reduce(
        (sum, modifier) => sum + modifier.healthMod,
        0,
      )
      s = updateInstance(s, instanceId, (i) => ({
        ...i,
        currentAttack: i.currentAttack - expiredAttack,
        currentHealth: Math.min(i.currentHealth, i.maxHealth - expiredHealth),
        maxHealth: i.maxHealth - expiredHealth,
        modifiers: remainingModifiers,
      }))
    }
  }

  return s
}

function startTurn(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
  turnNumber: number,
): MatchState {
  let s = state

  // Recover all units owned by this seat
  s = recoverSeatUnits(s, seatId, turnNumber)

  // Increase max energy (cap at energyCurveMax)
  const seat = getSeat(s, seatId)
  const newMaxEnergy = Math.min(
    seat.resources.maxEnergy + 1,
    s.config.energyCurveMax,
  )

  s = updateSeat(s, seatId, (seat) => ({
    ...seat,
    resources: { maxEnergy: newMaxEnergy, currentEnergy: newMaxEnergy },
    oncePerTurn: { hasUsedNormalSwap: false, hasRecruitedThisTurn: false },
  }))

  const chargeBonus = getChargeBonus(s, seatId)
  if (chargeBonus > 0) {
    s = updateSeat(s, seatId, (seat) => ({
      ...seat,
      resources: {
        ...seat.resources,
        currentEnergy: seat.resources.currentEnergy + chargeBonus,
      },
    }))
  }

  s = collectBatteryEnergy(s, seatId)

  // Draw up to hand size limit
  const currentHandSize = getSeat(s, seatId).zones.hand.length
  const cardsToDraw = Math.max(0, s.config.handSizeLimit - currentHandSize)
  s = drawCards(s, seatId, cardsToDraw)

  // Set turn
  s = {
    ...s,
    activeSeat: seatId,
    turn: {
      number: turnNumber,
      step: "main",
      actionWindow: "open",
      actionCount: 0,
    },
  }

  return s
}

function recoverSeatUnits(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
  turnNumber: number,
): MatchState {
  const seat = getSeat(state, seatId)
  const unitIds = [seat.zones.active, ...seat.zones.support].filter(
    (id): id is string => id !== null,
  )

  let s = state
  for (const id of unitIds) {
    s = updateInstance(s, id, (inst) => ({
      ...inst,
      currentHealth:
        inst.kind === "hero"
          ? inst.currentHealth
          : Math.min(inst.maxHealth, inst.currentHealth + 1),
      status: {
        exhausted: false,
        hasAttackedThisTurn: false,
        canAttackThisTurn:
          inst.status.jammedForTurnNumber === turnNumber
            ? false
            : inst.zone === "active" ||
              (inst.zone === "support" && inst.combatState.hasRanged),
        deployedThisTurn: false,
        remainingAttack:
          inst.status.jammedForTurnNumber === turnNumber
            ? 0
            : inst.zone === "active" ||
                (inst.zone === "support" && inst.combatState.hasRanged)
              ? inst.currentAttack
              : 0,
        jammedForTurnNumber: inst.status.jammedForTurnNumber,
      },
    }))
  }

  return s
}

function getChargeBonus(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): number {
  const seat = getSeat(state, seatId)
  let chargeCount = 0
  let generatorCount = 0

  for (const id of seat.zones.support) {
    const inst = state.instances[id]
    if (!inst) continue
    if (inst.combatState.hasCharge) chargeCount += 1
    if (inst.combatState.hasGenerator) generatorCount += 1
  }

  if (chargeCount === 0) return 0
  return chargeCount + Math.min(2, chargeCount * generatorCount)
}

function collectBatteryEnergy(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): MatchState {
  const seat = getSeat(state, seatId)
  const inPlay = [seat.zones.active, ...seat.zones.support].filter(
    (id): id is string => id !== null,
  )

  let totalBattery = 0
  let s = state

  for (const id of inPlay) {
    const inst = s.instances[id]
    if (!inst?.pendingBatteryEnergy) continue
    totalBattery += inst.pendingBatteryEnergy + 1
    s = updateInstance(s, id, (i) => ({
      ...i,
      pendingBatteryEnergy: undefined,
    }))
  }

  if (totalBattery === 0) return s

  return updateSeat(s, seatId, (seat) => ({
    ...seat,
    resources: {
      ...seat.resources,
      currentEnergy: seat.resources.currentEnergy + totalBattery,
    },
  }))
}
